import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import Button from "../../../../../../components/base/button/button";
import {Col, Row} from "reactstrap";
import {ArrowBack} from "@mui/icons-material";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import {Box, Paper} from "@mui/material";
import {Switch} from "../../../../../../components/base/switch";
import LazyImage from "../../../../../../components/app-specific/lazy-image";
import LazySvg from "../../../../../../components/app-specific/lazy-svg";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {OutlinedInputForm} from "../../../../../../components/base/input/outlined_input";
import Form from "../../../../../../components/base/form";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import SummaryInfoCard from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import TagConnectedParts from "./connected-parts";
import TagConnectedBanners from "./connected-banners";
import ManageTagIcon from "../../../../../../components/app-specific/manage-tag-icon";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useIndustry from "../../../../../../hooks/use-industry";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {useHistory, useParams} from "react-router-dom";
import {MuiRFF, Yup} from '../../../../../../components/form';
import useQuery from "../../../../../../hooks/use-query";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import UIUtils from "../../../../../../utils";
import CopyIconButton from "../../../../../../components/base/copy-to-clipboard/copy-icon-button";

const formNames = {
    title: 'title',
}

const schema = Yup.object().shape({
    [formNames.title]: Yup.string().trim().nullable().required(ValidateMessages.required),
})

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const TagSingleViewTabs = {
    connectedParts: 'connected-parts',
    connectedBanners: 'connected-banners',
}

const ProductsTagManagementSingleView = () => {
    const {industries, loadingIndustries} = useIndustry({noCategories: true});
    const history = useHistory();
    const params = useParams();
    const [, setQuery] = useQuery();
    const [loading, setLoading] = useState(true);
    const [deletingTag, setDeletingTag] = useState(false);
    const [tagInformation, setTagInformation] = useState(null);
    const [loadingTitleChange, setLoadingTitleChange] = useState(false);
    const [selectedTab, setSelectedTab] = useState(TagSingleViewTabs.connectedParts);
    const isMounted = useIsMounted();
    const initialRender = useRef(true);
    const {configureNotebookTags} = useConfigureNotebook('tag-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productsTagInformation(params.id));
        configureBehavioralTool((noteTags) => noteTags.productsTagInformation(params.id));
    }, [configureBehavioralTool, configureNotebookTags, params.id]);

    const tabs = useMemo(() => {
        const tabs = Object.values(TagSingleViewTabs).map(e => ({
            value: e,
            name: (function () {
                switch (e) {
                    case TagSingleViewTabs.connectedParts:
                        return 'Connected Parts';
                    case TagSingleViewTabs.connectedBanners:
                        return 'Connected Banners';
                    default:
                        return '';
                }
            })(),
        }));
        if (!operatorAccessLevels.packages.clientPlatform.bannerManagement) {
            tabs.splice(tabs.findIndex(e => e.value === TagSingleViewTabs.connectedBanners), 1);
        }
        return tabs;
    }, [operatorAccessLevels.packages.clientPlatform.bannerManagement])

    const selectedIndustry = useMemo(() => industries.find(e => e.id === tagInformation?.industryId), [industries, tagInformation?.industryId]);

    const tagBasicInformation = useMemo(() => ({
        body: [
            ...(industries?.length > 1 ? [
                {
                    id: '1',
                    title: 'Industry',
                    startAdornment:
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                            color: 'primary.main'
                        }}>
                            {
                                React.cloneElement(UIUtils.getIndustryIcon(selectedIndustry?.iconFileName, selectedIndustry?.id, true) ??
                                    <div/>, {style: {width: 24, height: 24, color: 'inherit'}})
                            }
                        </Box>
                    ,
                    value: loadingIndustries ? 'Loading...' : selectedIndustry?.title ?? "--",
                }
            ] : []),
            {
                id: '2',
                title: 'Connected Parts',
                value: tagInformation?.connectedPartCount,
            },
            {
                id: '3',
                title: 'Connected Banners',
                value: tagInformation?.connectedBannerCount,
            },
            {
                id: '4',
                title: 'Click Counts',
                value: tagInformation?.clickCount,
            },
        ],
    }), [industries?.length, selectedIndustry?.iconFileName, selectedIndustry?.id, selectedIndustry?.title, loadingIndustries, tagInformation?.connectedPartCount, tagInformation?.connectedBannerCount, tagInformation?.clickCount])

    /**
     * With each change in the selected tab resets the query params of the page
     */
    useLayoutEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
            return;
        }
        setQuery({}, {replace: true})
    }, [setQuery, selectedTab])

    /**
     * Calls the api for getting the information of a tag in case of success response, sets the [tagInformation] in the state
     * @type {(function(): Promise<void>)}
     */
    const getTagInformation = useCallback(async () => {
        const response = await Api.getTagFullInformation(parseInt(params.id));
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTagInformation({
                ...response.data,
                iconFileName: response.data.iconFileName
                    ? response?.configuration?.TagIconUrlBaseAddress?.concat(response.data.iconFileName) ?? ''
                    : null,
                banners: response?.data?.banners?.map(e => ({
                    ...e,
                    loading: false,
                    isDeleted: false,
                    fileName: e.fileName
                        ? response?.configuration?.BannerUrlBaseAddress?.concat(e.fileName) ?? ''
                        : null,
                })) ?? [],
            });
        }
        setLoading(false);
    }, [params.id, isMounted])

    /**
     * With each change in the url param:
     * - calls the function for getting the information of a tag
     */
    useEffect(() => {
        getTagInformation().then();
    }, [getTagInformation])

    /**
     * Deletes tag from the system:
     * - Calls the api regarding this action
     * - if the api call was successful, navigates user to the tags list
     * @type {(function(): Promise<void>)}
     */
    const deleteTagFromSystem = useCallback(async () => {
        setDeletingTag(true);
        const forApi = {
            ids: [
                parseInt(params.id)
            ]
        }
        const response = await Api.deleteTagsFromSystem(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            history.goBack();
        }
        setDeletingTag(false);
    }, [params.id, isMounted, history])

    /**
     * Opens confirmation dialog regarding deleting a tag
     * @type {(function(): void)}
     */
    const onGetDeleteConfirmation = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to delete this tag from the system?
                </p>
            </div>,
            {},
            (decision) => decision && deleteTagFromSystem(),
            "Delete",
            "Cancel",
        );
    }, [deleteTagFromSystem])

    /**
     * Changes the tag's title:
     * - Calls the api for updating the title of the tag
     * @type {(function(*): Promise<void>)}
     */
    const changeTagTitle = useCallback(async (title) => {
        setLoadingTitleChange(true);
        const forApi = {
            id: parseInt(params.id),
            name: title
        }
        const response = await Api.updateTagInformation(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTagInformation(prevState => ({
                ...prevState,
                name: response?.data?.name
            }))
        }
        setLoadingTitleChange(false);
    }, [params.id, isMounted])

    /**
     * Handles changes in the visibility of a tag for client switch
     * - Calls the api regarding this action
     * @type {(function(*): Promise<void>)}
     */
    const onVisibilityChanged = useCallback(async (isOn) => {
        setTagInformation(prevState => ({
            ...prevState,
            isVisibleForClient: isOn,
        }))
        const forApi = {
            id: parseInt(params.id),
            isVisibleForClient: isOn,
        }
        const response = await Api.updateTagInformation(forApi);
        if (!isMounted())
            return;
        if (!response?.resultFlag) {
            setTagInformation(prevState => ({
                ...prevState,
                isVisibleForClient: !isOn,
            }))
        }
    }, [isMounted, params.id])

    /**
     * Handles changes in the search-ability of tag switch
     * - Calls the api regarding this action
     * @type {(function(*): Promise<void>)}
     */
    const onSearchAbilityChanged = useCallback(async (isOn) => {
        setTagInformation(prevState => ({
            ...prevState,
            searchable: isOn,
        }))
        const forApi = {
            id: parseInt(params.id),
            searchable: isOn,
        }
        const response = await Api.updateTagInformation(forApi);
        if (!isMounted())
            return;
        if (!response?.resultFlag) {
            setTagInformation(prevState => ({
                ...prevState,
                searchable: !isOn,
            }))
        }
    }, [isMounted, params.id])

    /**
     * Handles changes in tag being public switch
     * - Calls the api regarding this action
     * @type {(function(*): Promise<void>)}
     */
    const onBeingPublicChanged = useCallback(async (isOn) => {
        setTagInformation(prevState => ({
            ...prevState,
            isPublic: isOn,
        }))
        const forApi = {
            id: parseInt(params.id),
            isPublic: isOn,
        }
        const response = await Api.updateTagInformation(forApi);
        if (!isMounted())
            return;
        if (!response?.resultFlag) {
            setTagInformation(prevState => ({
                ...prevState,
                isPublic: !isOn,
            }))
        }
    }, [isMounted, params.id])

    /**
     * Handles changing the tab and sets the [selectedTab] in the state
     * @type {(function(*, *): void)}
     */
    const onTabChanged = useCallback((e, value) => {
        setSelectedTab(value);
    }, [])

    /**
     * Handles updating the tag icon url
     * @type {(function(*): void)}
     */
    const onUpdateTagIconUrl = useCallback((newIconUrl) => {
        setTagInformation(prevState => ({
            ...prevState,
            iconFileName: newIconUrl
        }))
    }, [])

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center tag-single-view'>
                <div className={'d-flex flex-row justify-content-between align-items-center w-100 mt-4 mb-4 '}>
                    <div className={'title-section'}>
                        <IconButton
                            onPressed={() => history.goBack()}
                            color={'paper.secondary'}
                        >
                            <ArrowBack className='icon normal'/>
                        </IconButton>
                        <div className={'title-texts'}>
                            <p>
                                Back to Tag List
                            </p>
                        </div>
                    </div>
                    <Button
                        className='d-flex align-items-center justify-content-center remove-tag-button'
                        variant={'outlined'}
                        color={'error'}
                        disabled={deletingTag}
                        onClick={onGetDeleteConfirmation}
                    >
                        <RemoveIcon className={'remove-icon mr-2'}/>
                        <p className={'button-label'}>Delete the Tag</p>
                    </Button>
                </div>
                <Row className={'d-flex flex-row'}>
                    <Col xs={12} md={6} lg={12} xl={6} className={'mb-4'}>
                        <Paper className={'paper tag-summary-paper'}>
                            <SummaryInfoCard
                                body={tagBasicInformation.body}
                                loading={loading}
                                className={'tag-basic-info'}
                                loadingCount={tagBasicInformation.body.length}
                            />
                            <div
                                className={'d-flex flex-row justify-content-center align-items-center w-100 px-3 mb-4'}>
                                <div className={'tag-single-view-icon-container'}>
                                    {
                                        !!tagInformation?.iconFileName && (
                                            tagInformation?.iconFileName?.toLowerCase()?.endsWith('.png')
                                                ? <LazyImage
                                                    src={tagInformation?.iconFileName}
                                                    height={40}
                                                    width={40}
                                                />
                                                : <LazySvg
                                                    src={tagInformation?.iconFileName}
                                                    height={40}
                                                    width={40}
                                                />
                                        )
                                    }
                                </div>
                                <Form
                                    className={'d-flex flex-wrap w-85 mr-3'}
                                    onSubmit={() => false}
                                    id={'form-id'}
                                    validate={validate}
                                    initialValues={{
                                        [formNames.title]: tagInformation?.name,
                                        [formNames.industry]: tagInformation?.industryId,
                                    }}
                                    render={({values, form}) => {
                                        return (
                                            <>
                                                <Row className={'no-gutters align-items-center justify-content-center w-90'}>
                                                    <Col>
                                                        <OutlinedInputForm
                                                            fullWidth
                                                            name={formNames.title}
                                                            placeholder={'Type...'}
                                                            label={"Title"}
                                                            required={required[formNames.title]}
                                                        />
                                                    </Col>
                                                    <CopyIconButton value={tagInformation?.name}/>
                                                    <SaveOrRollback
                                                        column={false}
                                                        showText={false}
                                                        loading={loadingTitleChange}
                                                        show={!!(values[formNames.title] !== (tagInformation?.name ?? '') && (values[formNames.title]?.trim()?.length ?? false))}
                                                        onRollback={() => form.change(formNames.title, tagInformation?.name ?? '') || form.resetFieldState(formNames.title)}
                                                        onSave={() => changeTagTitle(values[formNames.title])}
                                                    />
                                                </Row>
                                            </>
                                        )
                                    }}
                                />
                            </div>
                            <div className={'tag-info-switches-container'}>
                                <div className={'d-flex flex-column justify-content-center align-items-center'}>
                                    <p className={'switch-title-indicator'}>
                                        Visible for Client
                                    </p>
                                    <Switch
                                        color={'primary'}
                                        checked={tagInformation?.isVisibleForClient ?? false}
                                        onChange={onVisibilityChanged}
                                    />
                                </div>
                                <div className={'d-flex flex-column justify-content-center align-items-center'}>
                                    <p className={'switch-title-indicator'}>
                                        Searchable
                                    </p>
                                    <Switch
                                        color={'primary'}
                                        checked={tagInformation?.searchable ?? false}
                                        onChange={onSearchAbilityChanged}
                                    />
                                </div>
                                <div className={'d-flex flex-column justify-content-center align-items-center'}>
                                    <p className={'switch-title-indicator'}>
                                        Public
                                    </p>
                                    <Switch
                                        color={'primary'}
                                        checked={tagInformation?.isPublic ?? false}
                                        onChange={onBeingPublicChanged}
                                    />
                                </div>
                            </div>
                        </Paper>
                    </Col>
                    <Col xs={12} md={6} lg={12} xl={6} className={'mb-4'}>
                        <Paper className='paper tag-icon-dropzone-wrapper'>
                            <div className={'tag-icon-dropzone-container'}>
                                <ManageTagIcon
                                    tagId={parseInt(params.id)}
                                    tagUrl={tagInformation?.iconFileName}
                                    updateTagIconUrl={onUpdateTagIconUrl}
                                />
                            </div>
                        </Paper>
                    </Col>
                </Row>
                <div className={'w-100 mr-4'}>
                    <Tabs
                        className={'mt-4'}
                        value={selectedTab}
                        onChange={onTabChanged}
                    >
                        {
                            tabs?.map((tab) => (
                                <Tab
                                    key={tab.value}
                                    value={tab.value}
                                    label={tab.name}
                                />
                            ))
                        }
                    </Tabs>
                </div>
                {
                    TagSingleViewTabs.connectedBanners === selectedTab &&
                    <TagConnectedBanners
                        connectedBanners={tagInformation?.banners}
                        setTagInfo={setTagInformation}
                        industry={selectedIndustry}
                        tagId={Number(params.id)}
                    />
                }
                {
                    TagSingleViewTabs.connectedParts === selectedTab &&
                    <TagConnectedParts
                        tagId={Number(params.id)}
                        setTagInfo={setTagInformation}
                        industry={selectedIndustry}
                    />
                }
            </Row>
        </>
    )
}

export default ProductsTagManagementSingleView;
